import * as zod from 'zod';

import type { BaseFeatureFlagDefinition, Section } from './featureFlags';

export type DevelopmentFeatureFlagDefinitionSchema =
  BaseFeatureFlagDefinition & {
    section?: Section;
    owner: string;
  };

// Feature flags moved from customer feature flags to development
export const DevelopmentFeatureFlagDataZod = zod.object({
  waypoints: zod.boolean().default(false),
  enableScrewdriverPickup: zod.boolean().default(false),
  enableLearnedModels: zod.boolean().default(false),
  complianceMode: zod.boolean().default(false),
  captureStep: zod.boolean().default(false),
  miniArm: zod.boolean().default(false),
  newCNCIntegration: zod.boolean().default(false),
  ewellixLiftTLT: zod.boolean().default(false),
  weldMachineIntegration: zod.boolean().default(false),
  enableCppGrippers: zod.boolean().default(true),
  localAccuracyCalibration: zod.boolean().default(false),
  customDevices: zod.boolean().default(false),
  customBase: zod.boolean().default(false),
  allowCustomBaseMatchJointAngles: zod.boolean().default(false),
  backups: zod.boolean().default(false),
  systemMonitorShutdown: zod.boolean().default(false),
  tooltipAcceleration: zod.boolean().default(false),
  enableWeldTooltipAccelerationEditing: zod.boolean().default(false),
  arcStepInMove: zod.boolean().default(true),
  disableArcStepInWeld: zod.boolean().default(false),
  onRobotHexE: zod.boolean().default(false),
  antigravityToggle: zod.boolean().default(false),
  antigravityStep: zod.boolean().default(false),
  ros2Bridge: zod.boolean().default(false),
  zmqPoseMigration: zod.boolean().default(false),
  useControlSourceManager: zod.boolean().default(false),
  disableElastokinIK: zod.boolean().default(false),
  singularityAvoidance: zod.boolean().default(false),
  useExpressionsForPayload: zod.boolean().default(false),
  allowSubstepsMoveStepV2: zod.boolean().default(false),
  disableWeldWeaving: zod.boolean().default(false),
  extraWeldModes: zod.boolean().default(false),
  motionPlanResampling: zod.boolean().default(false),
  treatEncoderIssuesAsWarnings: zod.boolean().default(false),
  treatPRCSyncAndInterpolatorErrorsAsWarnings: zod.boolean().default(true),
  treatShakeDetectedAsWarning: zod.boolean().default(false),
  treatWindupLUTNotCalculatedAsWarning: zod.boolean().default(false),
  motionPlanCompression: zod.boolean().default(false),
  waypointTimingScaling: zod.boolean().default(true),
  checkpointBasedLocate: zod.boolean().default(false),
  torqueAwareMotions: zod.boolean().default(false),
  usePayloadInertiaInDynamics: zod.boolean().default(true),
  useJerkLimits: zod.boolean().default(false),
  useOptimizerForJogging: zod.boolean().default(false),
  enableOptimizerLinearPlanner: zod.boolean().default(true),
  useCppCameraBot: zod.boolean().default(false),
  autoCameraCalibration: zod.boolean().default(false),
  stopEarlyIfForce: zod.boolean().default(false),
  disableDynamicHardwareConfig: zod.boolean().default(false),
  teleopSendHeadPose: zod.boolean().default(false),
  waypointOrientationGate: zod.boolean().default(false),
  unleashThor: zod.boolean().default(false),
  mountingBracket: zod.boolean().default(false),
  localInference: zod.boolean().default(false),
  enableRoboflowInference: zod.boolean().default(false),
  showDummyInferenceModel: zod.boolean().default(false),
  batchJogDirectionsValidation: zod.boolean().default(true),
  externalControlStep: zod.boolean().default(false),
  miraiRunSkill: zod.boolean().default(false),
  secondaryBotWebsocketState: zod.boolean().default(false),
  appRedesign: zod.boolean().default(true),
  redesignUiStubs: zod.boolean().default(false),
  redesignDarkModeSelection: zod.boolean().default(false),
  routineAgentChat: zod.boolean().default(false),
  lpfPlanner: zod.boolean().default(false),
  externalControlBridge: zod.boolean().default(false),
  persistentVariables: zod.boolean().default(true),
  nonPlanarLocate: zod.boolean().default(false),
  armMetricsTracking: zod.boolean().default(true),
  zmqBagRecorder: zod.boolean().default(true),
  disableRos2BagRecorder: zod.boolean().default(false),
  mujocoSimulator: zod.boolean().default(false),
  enableSkills: zod.boolean().default(false),
  aiPositioningSkill: zod.boolean().default(false),
  demonstrationRecording: zod.boolean().default(false),
  useTrajectoryController: zod.boolean().default(false),
  onlinePlannerJogging: zod.boolean().default(false),
  onlinePlannerStreaming: zod.boolean().default(false),
  useVirtualArm: zod.boolean().default(false),
  useDeterministicPlanner: zod.boolean().default(true),
  legacyMotionPlanCacheTolerance: zod.boolean().default(false),
  visionBotZmqMigration: zod.boolean().default(false),
  constantSpeed: zod.boolean().default(true),
  suppressSteps: zod.boolean().default(true),
  detection3dLocate: zod.boolean().default(false),
  dinoFastLocate: zod.boolean().default(false),
  dinoFastClassify: zod.boolean().default(false),
  reportActualJointPositions: zod.boolean().default(true),
  stereoDepth: zod.boolean().default(false),
  subroutines: zod.boolean().default(true),
  samplePositioningApproachStep: zod.boolean().default(false),
  insta360ImportEnabled: zod.boolean().default(false),
  useStagedDataProcessingPipeline: zod.boolean().default(false),
  matchRelativeToCurrentFrames: zod.boolean().default(true),
  routineSchemaImportExport: zod.boolean().default(false),
  useWindupTorqueV2: zod.boolean().default(true),
  useAutoTunedAntigravity: zod.boolean().default(false),
  useDynamicCollisionDetector: zod.boolean().default(false),
  useWindupTorqueForCollision: zod.boolean().default(false),
});

export type DevelopmentFeatureFlagData = zod.infer<
  typeof DevelopmentFeatureFlagDataZod
>;
export type DevelopmentFeatureFlagKey = keyof DevelopmentFeatureFlagData;

export const developmentFeatureFlagDefinitions: Record<
  DevelopmentFeatureFlagKey,
  DevelopmentFeatureFlagDefinitionSchema
> = {
  disableElastokinIK: {
    title: 'Disable Elastokin IK Solver',
    section: 'movement',
    allowSingleUser: false,
    description:
      'Fall back to the legacy ROS2 IK solver. Elastokin IK is on by default for every bot model, so this flag only needs enabling to turn it off.',
    owner: 'reyn',
  },
  singularityAvoidance: {
    // Hot-reloads in arm-control-bot: the trajectory controller reads this via
    // FeatureFlags::watchFeatureFlag(), which re-reads feature_flags.json on
    // change, so a toggle takes effect on the next SpeedL command with no
    // restart. Keep this false-by-default flag ANDed with tc-tuning.toml's
    // [singularity].enabled dark-launch guard on the C++ side.
    requiresRestart: false,
    title: 'Singularity Avoidance',
    section: 'movement',
    allowSingleUser: false,
    description:
      'Enable CBF-based singularity avoidance that scales SpeedL and jogging speeds down near shoulder/elbow/wrist singularities.',
    owner: 'mark',
  },
  waypoints: {
    requiresRestart: false,
    title: 'Old Waypoints',
    section: 'movement',
    allowSingleUser: true,
    owner: 'henry',
  },
  matchRelativeToCurrentFrames: {
    title: 'Match read/write frames for relative-to-current moves',
    section: 'movement',
    allowSingleUser: false,
    description:
      'Relative-to-current moves read the current pose and command the target in the same TCP frame, preventing flange↔TCP frame-mismatch nosedives.',
    owner: 'alex',
  },
  routineSchemaImportExport: {
    requiresRestart: false,
    title: 'Routine Schema Import/Export',
    section: 'other',
    allowSingleUser: false,
    description:
      'Show Download/Upload buttons in the routine Edit Schema modal for saving the schema as a .json file and loading a schema from a .json/.txt file.',
    owner: 'alex',
  },
  arcStepInMove: {
    title: 'Arc Step in Move',
    description: 'Enable the Arc Step inside Move Arm steps (still under test)',
    section: 'movement',
    allowSingleUser: false,
    owner: 'henry',
  },
  disableArcStepInWeld: {
    title: 'Disable Arc Step in Weld',
    description: 'Disable the Arc Step inside Weld steps (defaults to enabled)',
    section: 'movement',
    allowSingleUser: false,
    owner: 'henry',
  },
  batchJogDirectionsValidation: {
    requiresRestart: false,
    title: 'Batch jog direction validation',
    description: 'Use ROS batch service to validate jog buttons',
    section: 'movement',
    allowSingleUser: true,
    owner: 'reyn',
  },
  enableScrewdriverPickup: {
    requiresRestart: false,
    title: 'OnRobot Screwdriver pickup',
    section: 'equipment',
    allowSingleUser: false,
    owner: 'paige',
  },
  enableLearnedModels: {
    title: 'Enable Learned Models',
    description: 'Enable all AI learned-model features',
    section: 'AI',
    owner: 'kavan',
  },
  complianceMode: {
    title: 'Compliance mode',
    description:
      'Run compliance controller as the pose-based real-time controller',
    section: 'AI',
    owner: 'jacob',
  },
  captureStep: {
    title: 'Capture Step',
    description: 'Enable the Capture Step',
    section: 'vision',
    allowSingleUser: true,
    owner: 'raphael',
  },
  miniArm: {
    title: 'Mini Arm',
    description: 'Add mini-arm control',
    owner: 'connor',
  },
  newCNCIntegration: {
    title: 'New CNC integrations in equipment manager',
    section: 'equipment',
    allowSingleUser: true,
    owner: 'christine',
  },
  ewellixLiftTLT: {
    requiresRestart: false,
    title: 'Ewellix Lift TLT',
    section: 'equipment',
    allowSingleUser: true,
    owner: 'christine',
  },
  weldMachineIntegration: {
    requiresRestart: false,
    title: 'Weld machine integration',
    section: 'equipment',
    allowSingleUser: true,
    owner: 'henry',
  },
  localAccuracyCalibration: {
    title: 'Local Accuracy Calibration',
    section: 'environmentAndSpace',
    allowSingleUser: false,
    description:
      'Allows the user to calibrate the accuracy of the arm when moving to positions.',
    owner: 'shreyas',
  },
  customDevices: {
    title: 'Custom Devices',
    section: 'equipment',
    allowSingleUser: false,
    description: 'Enable custom devices',
    owner: 'christine',
  },
  customBase: {
    title: 'Custom Base',
    section: 'equipment',
    allowSingleUser: false,
    description: 'Enable custom robot base/mounting configuration',
    owner: 'jacob',
  },
  allowCustomBaseMatchJointAngles: {
    title: 'Allow Custom Base Match Joint Angles',
    section: 'equipment',
    allowSingleUser: false,
    description: 'Allow to match joint angles when a custom base is enabled',
    owner: 'jacob',
  },
  backups: {
    requiresRestart: false,
    title: 'Backups',
    section: 'infra',
    allowSingleUser: false,
    description: 'Show backups option in robot menu',
    owner: 'shreyas',
  },
  systemMonitorShutdown: {
    title: 'System Monitor Shutdown',
    section: 'safety',
    allowSingleUser: false,
    description: 'Shut down control box when system reaches critical levels',
    owner: 'shreyas',
  },
  tooltipAcceleration: {
    requiresRestart: false,
    title: 'Tooltip Acceleration',
    section: 'movement',
    description: 'Enable tooltip acceleration controls for safety settings',
    owner: 'henry',
  },
  enableWeldTooltipAccelerationEditing: {
    requiresRestart: false,
    title: 'Weld Tooltip Acceleration Editing',
    section: 'movement',
    description:
      'Allow editing tooltip acceleration in weld steps. When off, acceleration is fixed at 50 mm/s².',
    owner: 'henry',
  },
  antigravityToggle: {
    requiresRestart: false,
    title: 'Antigravity Toggle',
    section: 'movement',
    allowSingleUser: true,
    description: 'Enable non-wrist antigravity toggle',
    owner: 'paige',
  },
  antigravityStep: {
    title: 'Antigravity Step',
    section: 'movement',
    allowSingleUser: false,
    description: 'Enable Antigravity Step',
    owner: 'jacob',
  },
  onRobotHexE: {
    title: 'OnRobot Hex-E',
    section: 'equipment',
    allowSingleUser: false,
    description: 'Enable OnRobot Hex-E integration',
    owner: 'jacob',
  },
  ros2Bridge: {
    title: 'ROS2 Bridge',
    section: 'infra',
    allowSingleUser: false,
    description:
      'Enable `ros2-bridge-bot` to bridge ROS2 topics to the local network',
    owner: 'shreyas',
  },
  zmqPoseMigration: {
    title: 'ZMQ Pose Migration',
    section: 'infra',
    allowSingleUser: false,
    description: 'Enable migration of pose messaging to ZMQ',
    owner: 'jacob',
  },
  useControlSourceManager: {
    title: 'Control Source Manager',
    section: 'movement',
    allowSingleUser: false,
    description:
      'Use the control source manager to manage what pose source can control the arm.',
    owner: 'connor',
  },
  enableCppGrippers: {
    title: 'C++ Gripper Control',
    section: 'equipment',
    allowSingleUser: false,
    description:
      'Enable low-latency C++ gripper control in ACB instead of TypeScript drivers (on by default)',
    owner: 'aneek',
  },
  useExpressionsForPayload: {
    requiresRestart: false,
    title: 'Use Expressions for Payload',
    section: 'movement',
    allowSingleUser: true,
    description: 'Enable use of expressions for payload',
    owner: 'reyn',
  },
  allowSubstepsMoveStepV2: {
    title: 'Allow certain new substeps in move step v2',
    section: 'movement',
    allowSingleUser: true,
    description: 'Enable allow set io and set variable move step v2',
    owner: 'reyn',
  },
  disableWeldWeaving: {
    requiresRestart: false,
    title: 'Disable Weld Weaving',
    section: 'movement',
    allowSingleUser: true,
    description:
      'Disable the weaving option in the weld step wizard (defaults to enabled on Core, disabled on Thor)',
    owner: 'santiago',
  },
  extraWeldModes: {
    requiresRestart: false,
    title: 'Extra Weld Modes',
    section: 'equipment',
    allowSingleUser: true,
    description:
      'Offer the HD MIG and RMD processes in the Weld step wizard. A step already saved with one of them keeps offering it regardless of this flag.',
    owner: 'ryanwalek',
  },
  motionPlanResampling: {
    title: 'Motion Plan Resampling',
    section: 'movement',
    allowSingleUser: false,
    description: 'Enable resampling of weaving motion plans',
    owner: 'santiago',
  },
  treatEncoderIssuesAsWarnings: {
    title: 'Treat Encoder Issues as Warnings',
    section: 'safety',
    allowSingleUser: false,
    description: 'Treat encoder issues as warnings instead of errors',
    owner: 'paige',
  },
  treatPRCSyncAndInterpolatorErrorsAsWarnings: {
    title: 'Treat PRC Sync and Interpolator Error as Warnings',
    section: 'safety',
    allowSingleUser: false,
    description:
      'Treat PRC sync and interpolator error as warnings instead of errors',
    owner: 'paige / ryan',
  },
  treatShakeDetectedAsWarning: {
    title: 'Treat Shake Detected as Warning',
    section: 'safety',
    allowSingleUser: false,
    description:
      'Treat ShakeDetected ResultType as a warning instead of an error',
    owner: 'ryan',
  },
  treatWindupLUTNotCalculatedAsWarning: {
    title: 'Treat Windup LUT Not Calculated as Warning',
    section: 'safety',
    allowSingleUser: false,
    description:
      'Treat missing windup calibration (WindupLUTNotCalculated) as a warning instead of a violation',
    owner: 'aneek',
  },
  motionPlanCompression: {
    title: 'Motion Plan Compression',
    section: 'movement',
    allowSingleUser: false,
    description: 'Enable compression for motion plan cache',
    owner: 'freddy',
  },
  waypointTimingScaling: {
    title: 'Different speed per waypoints',
    section: 'movement',
    allowSingleUser: false,
    description: 'Enable per-waypoint timing scaling control in Move Arm',
    owner: 'lovro',
  },
  checkpointBasedLocate: {
    title: 'Checkpoint-based Locate',
    section: 'AI',
    allowSingleUser: false,
    description:
      'Enable checkpoint-based locate functionality for improved object detection and positioning',
    owner: 'kavan',
  },
  torqueAwareMotions: {
    title: 'Torque-aware motions',
    section: 'movement',
    allowSingleUser: false,
    description: 'Enable motion planning using torque limits',
    owner: 'jacob',
  },
  usePayloadInertiaInDynamics: {
    requiresRestart: false,
    title: 'Payload inertia in dynamics',
    section: 'movement',
    allowSingleUser: false,
    description:
      'Model payload as end-effector segment inertia so KDL inverse-dynamics accounts for both gravity (m·g) and inertial (m·r²·α) contributions on every joint, instead of only applying a static m·g wrench at the EE. Affects expectedTorque used by windup-cal training and collision detection.',
    owner: 'brian',
  },
  useJerkLimits: {
    title: 'Use Jerk Limits',
    section: 'movement',
    allowSingleUser: false,
    description: 'Enable use of jerk limits for motion planning',
    owner: 'lovro',
  },
  useOptimizerForJogging: {
    title: 'Use Optimizer for Jogging',
    section: 'movement',
    allowSingleUser: false,
    description:
      'Use the path-preserving optimizer instead of TOTG for jogging time parametrization. Preserves exact waypoint positions without corner-rounding or resampling.',
    owner: 'lovro',
  },
  enableOptimizerLinearPlanner: {
    title: 'Optimizer Linear Planner',
    section: 'movement',
    allowSingleUser: false,
    description:
      'Include OptimizerLinearPlanner in the multi-planner for TYPE_LINEAR connections',
    owner: 'reyn',
  },
  useCppCameraBot: {
    title: 'Use C++ Camera Bot',
    section: 'AI',
    allowSingleUser: false,
    description: 'Switch to use C++ camera bot (leaves legacy for wrist cam)',
    owner: 'cnr',
  },
  autoCameraCalibration: {
    title: 'Automatic Camera Calibration',
    section: 'AI',
    allowSingleUser: false,
    description:
      'Enable the streaming "calibration mode" for external camera intrinsics/extrinsics (auto-capture + live coverage).',
    owner: 'kavan',
  },
  stopEarlyIfForce: {
    title: 'Stop Early If Force',
    section: 'movement',
    allowSingleUser: false,
    description: 'Enable stop early if force for waypoints',
    owner: 'rox',
  },
  disableDynamicHardwareConfig: {
    title: 'Disable Dynamic Hardware Config',
    section: 'equipment',
    allowSingleUser: false,
    description:
      'Disable loading dynamic hardware config from firmware (defaults to enabled)',
    owner: 'aneek',
  },
  teleopSendHeadPose: {
    title: 'Teleop Head Pose UDP',
    section: 'AI',
    allowSingleUser: false,
    description: 'Enable POC head tracking via udp',
    owner: 'cnr',
  },
  waypointOrientationGate: {
    title: 'Waypoint Orientation Gate',
    description:
      'Require orientation within thresholds (in addition to distance) to mark waypoint reached',
    section: 'movement',
    allowSingleUser: false,
    owner: 'derekhe',
  },
  unleashThor: {
    title: 'Unleash Thor',
    section: 'safety',
    allowSingleUser: false,
    description:
      'Enables the Faster safety preset and unlocks the full speed/acceleration range for Thor arms.',
    owner: 'aneek',
  },
  mountingBracket: {
    requiresRestart: false,
    title: 'Mounting Bracket',
    section: 'equipment',
    allowSingleUser: false,
    description:
      'Enable mounting bracket end effector equipment for mounting adapters',
    owner: 'jacob',
  },
  localInference: {
    title: 'Local Inference',
    section: 'AI',
    allowSingleUser: false,
    description:
      'Enable running AI inference locally (on GPU when present, otherwise CPU) without an external GPU Task Runner',
    owner: 'derek',
  },
  enableRoboflowInference: {
    title: 'Roboflow Inference',
    section: 'vision',
    allowSingleUser: false,
    description:
      'Show the Roboflow model source when configuring a locate skill, so a Roboflow-hosted model can run on-robot via the roboflow-inference-bot container. UI-only: the container image is pre-staged AND the daemon runs on echo/gpu-mode amd64 robots by operation mode, independent of this flag.',
    owner: 'raphael',
  },
  showDummyInferenceModel: {
    requiresRestart: false,
    title: 'Show dummy inference model',
    section: 'AI',
    allowSingleUser: true,
    description:
      'Show a dummy circle model option in the model selector for camera-free sim testing',
    owner: 'kavan',
  },
  externalControlStep: {
    title: 'External Control Step',
    section: 'movement',
    allowSingleUser: false,
    description: 'Enable external control step',
    owner: 'jacob',
  },
  miraiRunSkill: {
    title: 'MIRAI Run Skill',
    section: 'AI',
    allowSingleUser: false,
    description: 'Enable MIRAI Controller equipment and MIRAI Run Skill step',
    owner: 'jacob',
  },
  secondaryBotWebsocketState: {
    title: 'Secondary Bot WebSocket State',
    section: 'equipment',
    allowSingleUser: false,
    description:
      'Enable subscribing to secondary bot RoutineRunnerState via WebSocket in StandardBotsRO1 integration',
    owner: 'michael',
  },
  appRedesign: {
    requiresRestart: false,
    title: 'App Redesign',
    section: 'newUI',
    allowSingleUser: false,
    description: 'Enable the redesigned app UI',
    owner: 'christine',
  },
  redesignUiStubs: {
    requiresRestart: false,
    title: '[Eng only] Post-Automate Redesign UI Stubs',
    section: 'newUI',
    allowSingleUser: false,
    description:
      'Show not-yet-functional redesign UI (Simulate/Step/Skip-step/Anti-Gravity buttons, Safety/Network/Help settings tabs, and placeholder joint/motion metrics). Requires `appRedesign`. When off, these stubs are hidden.',
    owner: 'christine',
  },
  redesignDarkModeSelection: {
    requiresRestart: false,
    title: 'Redesign Dark Mode Selection',
    section: 'newUI',
    allowSingleUser: false,
    description:
      'Show the Light/Dark appearance selector in the redesigned General settings. Requires `appRedesign` to also be on. When off, the selector is hidden and the appearance preference is left as-is.',
    owner: 'christine',
  },
  routineAgentChat: {
    requiresRestart: false,
    title: 'Routine Agent Chat',
    section: 'newUI',
    allowSingleUser: false,
    description:
      'Enable the in-app Routine Agent chat panel. Requires `appRedesign` to also be on — the chat panel only mounts inside the redesign layout.',
    owner: 'santiago',
  },
  lpfPlanner: {
    title: 'LPF Planner',
    section: 'movement',
    description:
      'Use low-pass filter planner instead of spline planner for streaming interfaces',
    owner: 'Alex',
  },
  persistentVariables: {
    title: 'Persistent Variables',
    section: 'environmentAndSpace',
    description:
      'Allow environment variables to retain their last runtime value across routine executions',
    owner: 'roxanne',
  },
  armMetricsTracking: {
    title: 'Enable Arm Metrics Tracking',
    section: 'infra',
    allowSingleUser: false,
    description: 'Enable robodometer reporting for metrics tracking',
    owner: 'shreyas',
  },
  zmqBagRecorder: {
    title: 'ZMQ Bag Recorder & Metrics',
    section: 'infra',
    allowSingleUser: false,
    description:
      'Enable ZMQ bag recorder (MCAP recording) and messenger metrics counters. Default on; disable to turn off observability overhead.',
    owner: 'jacob',
  },
  disableRos2BagRecorder: {
    title: 'Disable ROS2 Bag Recorder',
    section: 'movement',
    allowSingleUser: false,
    description:
      'When enabled, forces the ROS2 bag recorder off on control boxes (where it would otherwise run by default). No effect in dev/VM environments.',
    owner: 'reyn',
  },
  nonPlanarLocate: {
    title: 'Non-Planar Locate',
    section: 'vision',
    allowSingleUser: false,
    description: 'Enable non-planar plane estimation in the Locate step.',
    owner: 'derek',
  },
  mujocoSimulator: {
    title: 'MuJoCo Simulator',
    section: 'AI',
    allowSingleUser: false,
    description:
      'Enable MuJoCo simulator features: virtual camera discovery, scene object API for codeblocks',
    owner: 'lee',
  },
  externalControlBridge: {
    title: 'External Control Bridge',
    section: 'infra',
    allowSingleUser: false,
    description:
      'Enable UDP real-time streaming bridge in external-control-bot',
    owner: 'jacob',
  },
  constantSpeed: {
    requiresRestart: false,
    title: 'Constant Speed',
    section: 'movement',
    allowSingleUser: false,
    description:
      'Enable constant speed mode for Move Arm steps, routing motions through the Cartesian planner',
    owner: 'santiago',
  },
  enableSkills: {
    title: 'Enable Skills',
    section: 'AI',
    allowSingleUser: false,
    description:
      'Enable the self-serve Skills experience (skill wizard, skill list, and skill steps)',
    owner: 'kavan',
  },
  aiPositioningSkill: {
    requiresRestart: false,
    title: 'AI Positioning Skill',
    section: 'AI',
    allowSingleUser: false,
    description:
      'Gate the ability for the skill wizard to create a positioning skill',
    owner: 'kavan',
  },
  demonstrationRecording: {
    title: 'Demonstration Recording',
    section: 'AI',
    allowSingleUser: false,
    description:
      'Enable Demonstration Skill: record a hand-guided trajectory once and replay it later, pinned to a chosen anchor frame.',
    owner: 'jacob',
  },
  useTrajectoryController: {
    title: 'Online Planning',
    section: 'movement',
    description:
      'Online planning master switch: enables the 1kHz trajectory controller in arm-control-bot (module, connector, prerequisite pushes). Consumers opt in per feature via Online Planning — Jogging and Online Planning — Streaming.',
    owner: 'freddy',
  },
  onlinePlannerJogging: {
    title: 'Online Planning — Jogging',
    section: 'movement',
    description:
      'Online planning for jogging: route SpeedJ/SpeedL jogs through the trajectory controller. Only takes effect when Online Planning (useTrajectoryController) is on; leave off to keep jogging on the legacy path while streaming uses the TC.',
    owner: 'freddy',
  },
  onlinePlannerStreaming: {
    title: 'Online Planning — Streaming',
    section: 'movement',
    allowSingleUser: false,
    description:
      'Online planning for external-control joint streaming: route it through the trajectory controller (ServoJ) instead of the direct WriteJointTrajectory path. Requires Online Planning (useTrajectoryController) and zmqPoseMigration.',
    owner: 'freddy',
  },
  useVirtualArm: {
    title: 'Virtual Arm in arm-control-bot',
    section: 'movement',
    description:
      'Move the canonical source of truth for vizbot joint state into a SimulatedJointState container inside arm-control-bot. Botman pushes positions on every state change (jog, lerp tick, teleport) and subscribes to a JointState topic to keep its local cache in sync. Phase A — no TrajectoryController involvement yet.',
    owner: 'connor',
  },
  useDeterministicPlanner: {
    title: 'Maintain robot configuration',
    section: 'movement',
    allowSingleUser: true,
    description:
      'Show the "Maintain robot configuration" toggle on every waypoint regardless of motion kind (joint, line, rotation). When the toggle is on: joint motions use the deterministic Pilz PTP planner (no RRT fallback), and every motion restricts IK to the upstream waypoint\'s configuration branch (overhead position / elbow up-or-down / wrist sign) so the arm holds the same configuration end-to-end.',
    owner: 'reyn',
  },
  legacyMotionPlanCacheTolerance: {
    title: 'Legacy Motion Plan Cache Tolerance',
    section: 'movement',
    allowSingleUser: false,
    description:
      'Use tighter start joint tolerance (0.002 rad) for the motion plan cache, for old firmware with less position settling tolerance',
    owner: 'reyn-sb',
  },
  visionBotZmqMigration: {
    title: 'Vision Bot ZMQ Migration',
    section: 'vision',
    allowSingleUser: false,
    description:
      'Route all vision-bot RPCs through ZMQ instead of Thrift. Requires GPUTaskRunner.',
    owner: 'derek',
  },
  suppressSteps: {
    requiresRestart: false,
    title: 'Suppress Steps',
    section: 'movement',
    allowSingleUser: false,
    description:
      'Allow suppressing routine steps so they remain in the tree but are skipped during execution.',
    owner: 'ryanwalek',
  },
  detection3dLocate: {
    requiresRestart: false,
    title: '3D Detection Locate',
    section: 'vision',
    allowSingleUser: false,
    description:
      'Enable 3D detection locate method using text prompt + depth-based OBB pose estimation',
    owner: 'raphael',
  },
  dinoFastLocate: {
    requiresRestart: false,
    title: 'ClickFind AI Locate',
    section: 'vision',
    allowSingleUser: true,
    description:
      'Enable ClickFind AI locate method (visual similarity to a clicked target region using patch embeddings)',
    owner: 'kavan',
  },
  dinoFastClassify: {
    requiresRestart: false,
    title: 'Match AI Classify',
    section: 'vision',
    allowSingleUser: true,
    description:
      'Enable Match AI classify method (image classification via full-image embeddings and cosine similarity)',
    owner: 'kavan',
  },
  reportActualJointPositions: {
    title: 'Report Actual Joint Positions',
    section: 'movement',
    allowSingleUser: false,
    description:
      'arm-control-bot reports measured encoder positions instead of commanded setpoints in its state stream. Required for routine-runner collision detection to see simulator positionDeltas and real-hardware drift; exposes encoder noise to the collision loop.',
    owner: 'reyn',
  },
  stereoDepth: {
    title: 'Stereo Depth',
    section: 'vision',
    allowSingleUser: false,
    description: 'Enable depth processing for stereo camera pairs',
    owner: 'derek',
  },
  subroutines: {
    title: 'Subroutines',
    section: 'movement',
    description:
      'Allow calling other routines as subroutines from within a routine',
    owner: 'roxanne',
  },
  samplePositioningApproachStep: {
    title: 'Sample Positioning Approach Step',
    section: 'AI',
    allowSingleUser: false,
    description:
      'Enable the SamplePositioningApproach step (positioning-skill data collection)',
    owner: 'kavan',
  },
  insta360ImportEnabled: {
    title: 'Insta360 SD-card Upload Pipeline',
    section: 'AI',
    allowSingleUser: false,
    description:
      'Enable echo-bot HandheldImportService — polls /etc/standardbots/skills/echo-recording/insta360-upload/ for .insv recordings, looks up per-camera calibration on feathers-bot, and routes them through UploadManager into the cloud SLAM stage.',
    owner: 'derek',
  },
  useStagedDataProcessingPipeline: {
    title: 'Staged Data-Processing Pipeline',
    section: 'AI',
    allowSingleUser: false,
    description:
      "Route this control box's training-data session uploads through the staged data-processing pipeline (per-stage Cloud Run workers) instead of the monolith processor job. echo-bot reads it at upload time and forwards it to api-srv.",
    owner: 'raphael',
  },
  useWindupTorqueV2: {
    title: 'Use Windup Torque V2 (ML)',
    section: 'movement',
    allowSingleUser: false,
    description:
      'Master enable for the windup-torque V2 (ML estimate) pipeline. Only joints whose per-arm config sets `windupTorqueV2Capable: true` will actually consume the V2 signal; joints not opted in keep the legacy `windupTorque` regardless of this flag. Joints that opt in but whose firmware reports an UNCALIBRATED ML model fall back to the legacy `windupTorque` at runtime and emit a WARNING event so the operator can flash a model. Disable to force ALL joints back to legacy.',
    owner: 'brian',
  },
  useAutoTunedAntigravity: {
    title: 'Auto-tuned antigravity',
    section: 'movement',
    allowSingleUser: false,
    description:
      'Replace the static per-joint antigravity disturbance deadband with one auto-tuned to the live windup-torque noise floor, and switch the in-AG baseline between a stationary capture (e_static) and a moving capture (e_moving) to absorb the windupTorqueV2 stationary-vs-moving bias step. Off by default falls back to the legacy static-threshold + single-baseline path.',
    owner: 'brian',
  },
  useDynamicCollisionDetector: {
    title: 'Dynamic collision detector',
    section: 'movement',
    allowSingleUser: false,
    description:
      'arm-control-bot replaces the static per-joint collision threshold with a lagged moving-average detector: threshold(t) = min(MA(|d|, [t-lag-window, t-lag]) + headroomNm, maxThresholdNm). The MA tracks the natural pose-dependent disturbance envelope (the V2 windup model is more accurate during motion than static hold); subtracting it leaves only the spike component, so the headroom directly sets the minimum detectable collision magnitude (~20 Nm by default). Sustained collisions stay latched because the MA freezes when above threshold. Per-joint: headroomNm, maxThresholdNm; arm-wide: lagMs, windowMs. Orthogonal to the windup-vs-amperage torque-source flag.',
    owner: 'brian',
  },
  useWindupTorqueForCollision: {
    title: 'Compare collision disturbance against windup torque',
    section: 'movement',
    allowSingleUser: false,
    description:
      'arm-control-bot uses windupTorque instead of amperageTorque as the measured-torque source feeding the collision-detection comparison. Only joints whose per-arm config sets `windupTorqueForCollisionCapable: true` switch; joints not opted in keep amperageTorque regardless of this flag. Switches which disturbance signal is published downstream as well so logs stay consistent. Orthogonal to the threshold-strategy flag (useDynamicCollisionDetector).',
    owner: 'brian',
  },
};

type GroupedKeys = { section: Section; keys: DevelopmentFeatureFlagKey[] }[];

export const underDevelopmentFeatureFlags: GroupedKeys = (() => {
  const grouped: GroupedKeys = [];

  for (const keyStr of Object.keys(developmentFeatureFlagDefinitions)) {
    const key = keyStr as DevelopmentFeatureFlagKey;
    let { section } = developmentFeatureFlagDefinitions[key];

    if (!section) {
      section = 'other';
    }

    const group = grouped.find((g) => g.section === section);

    if (group) {
      group.keys.push(key);
    } else {
      grouped.push({ section, keys: [key] });
    }
  }

  // sort by section
  return grouped.sort((a, b) => {
    if (a.section === 'other') {
      return 1;
    }

    if (b.section === 'other') {
      return -1;
    }

    return a.section.localeCompare(b.section);
  });
})();
